<script>
    import I18n from '@js/modules/translations';
    import {activateOnKey} from '@js/modules/a11y';

    import Input from '@js/components/themes/Input.svelte';
    import Dropdown from '@js/components/themes/Dropdown.svelte';
    import Icon from '@js/components/themes/Icon.svelte';
    import dateIcon from '@js/components/icons/dateIcon';

    import {PlanDateTypes} from '@js/components/modules/constants';
    import {PLAN_PERIOD_COUNTS, periodOptions, periodStart, periodLabel, parseIsoDate, toIsoDate, planDateTypeLabel} from '@js/modules/plan/planPeriods';

    const {
        class: className,
        nameDateType,
        namePlannedAt,
        namePerformedAt,
        labelDateType = I18n.t('js.ride.plan.model.plan_date_type'),
        labelPlannedAt = I18n.t('js.ride.plan.model.planned_at'),
        labelPerformedAt = I18n.t('js.ride.model.performed_at'),
        defaultDateType = 'unplanned',
        defaultPlannedAt,
        defaultPerformedAt,
        onDateTypeChange,
        isCompletedExclude = false,
        // The plan place form only: a week, month or year is a dated period to pick (the ride form keeps "This week")
        withPeriods = false
        // onPlannedAtChange
    } = $props();

    let planDateType = $state(defaultDateType);

    // A week, month or year is a dated period: the one already chosen, else the current one.
    const _periodFor = (dateType) => {
        if (!withPeriods || !PLAN_PERIOD_COUNTS[dateType]) {
            return null;
        }

        if (dateType === defaultDateType && defaultPlannedAt) {
            return toIsoDate(periodStart(dateType, parseIsoDate(defaultPlannedAt)));
        }

        return periodOptions(dateType)[0];
    };

    // The form is mounted per opened place: the initial period is the one to edit.
    const _initialPeriod = () => _periodFor(defaultDateType);

    let planPeriod = $state(_initialPeriod());

    const periodChoices = $derived(periodOptions(planDateType, new Date(), planPeriod));
    const currentPeriod = $derived(periodOptions(planDateType)[0]);

    const _handlePeriodChange = (period, event) => {
        event.preventDefault();

        planPeriod = period;
    };

    const _handlePlanDateTypeChange = (dateType, event) => {
        event.preventDefault();

        planDateType = dateType;
        planPeriod = _periodFor(dateType);

        if (onDateTypeChange) {
            onDateTypeChange(dateType);
        }
    };

    let planDateTypes = $state(PlanDateTypes);
    if (isCompletedExclude) {
        planDateTypes = PlanDateTypes.slice(0, -1);
    }
</script>

<div class={className}>
    <Dropdown class="mx-auto w-full"
              buttonClass="w-full px-4 py-2 flex justify-between rounded-lg text-sm"
              color="gray"
              name={nameDateType}
              label={labelDateType}
              selected={planDateType}
              optionSize={planDateTypes.length}
              closeClickInside={true}>
        {#snippet dropdownButtonRender()}
        <span class="flex items-center justify-center">
            <Icon class="inline-flex h-4 w-4 me-2"
                  icon={dateIcon}/>
            {withPeriods ? planDateTypeLabel(planDateType) : I18n.t(`js.ride.plan.enums.plan_date_type.${planDateType}`)}
        </span>
        {/snippet}

        <ul class="text-sm text-gray-700 dark:text-gray-200"
            role="listbox"
            aria-labelledby="DateType">
            {#each planDateTypes as dateType (dateType)}
                <li class="flex items-center justify-start p-2 cursor-pointer hover:bg-gray-300"
                    class:bg-primary-200={planDateType === dateType}
                    role="option"
                    aria-selected={planDateType === dateType}
                    tabindex="0"
                    onclick={_handlePlanDateTypeChange.bind(null, dateType)}
                    onkeydown={activateOnKey(_handlePlanDateTypeChange.bind(null, dateType))}>
                    {withPeriods ? planDateTypeLabel(dateType) : I18n.t(`js.ride.plan.enums.plan_date_type.${dateType}`)}
                </li>
            {/each}
        </ul>
    </Dropdown>

    {#if withPeriods && PLAN_PERIOD_COUNTS[planDateType]}
        <Dropdown class="mx-auto my-2 w-full"
                  buttonClass="w-full px-4 py-2 flex justify-between rounded-lg text-sm"
                  color="gray"
                  name={namePlannedAt}
                  label={I18n.t(`js.ride.plan.periods.label.${planDateType}`)}
                  selected={planPeriod}
                  optionSize={periodChoices.length}
                  closeClickInside={true}>
            {#snippet dropdownButtonRender()}
                <span class="flex items-center justify-center">
                    <Icon class="inline-flex h-4 w-4 me-2"
                          icon={dateIcon}/>
                    {periodLabel(planDateType, planPeriod)}
                </span>
            {/snippet}

            <ul class="max-h-64 overflow-y-auto text-sm text-gray-700 dark:text-gray-200"
                role="listbox"
                aria-label={I18n.t(`js.ride.plan.periods.label.${planDateType}`)}>
                {#each periodChoices as period (period)}
                    <li class="flex items-center justify-between gap-2 p-2 cursor-pointer hover:bg-gray-300"
                        class:bg-primary-200={planPeriod === period}
                        role="option"
                        aria-selected={planPeriod === period}
                        tabindex="0"
                        onclick={_handlePeriodChange.bind(null, period)}
                        onkeydown={activateOnKey(_handlePeriodChange.bind(null, period))}>
                        {periodLabel(planDateType, period)}

                        {#if period < currentPeriod}
                            <span class="text-xs font-medium text-yellow-800">
                                {I18n.t('js.ride.plan.periods.past')}
                            </span>
                        {/if}
                    </li>
                {/each}
            </ul>
        </Dropdown>
    {/if}

    {#if planDateType === 'date'}
        <Input type="date"
               class="mx-auto my-2 w-full"
               inputClass="py-2"
               name={namePlannedAt}
               label={labelPlannedAt}
               defaultValue={defaultPlannedAt}
               onfocus={(event) => event.target.showPicker()}>
        </Input>
    {/if}

    {#if planDateType === 'completed'}
        <Input type="date"
               class="mx-auto my-2 w-full"
               inputClass="py-2"
               name={namePerformedAt}
               label={labelPerformedAt}
               defaultValue={defaultPerformedAt}
               onfocus={(event) => event.target.showPicker()}>
        </Input>
    {/if}
</div>
